import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, Subscription } from 'rxjs';

import {
    changeSearchTypes,
    searchChangeCategoryPageNum,
} from 'SearchAllApp/state/search-all-params/search-all-params.actions';
import {
    getCategoryPageNum,
    getQueryInput,
    getSearchTypes,
} from 'SearchApp/ts/apps/search-all-app/state/search-all.selectors';

import { SEARCH_ALL_METHODS } from 'SearchApp/ts/constants';
import {
    SearchAllCategory,
    SearchCategoryPagination,
    SearchTypeInfoPagination,
} from 'SearchApp/ts/types/search-all-category.type';
import { SearchAllRootStore } from 'SearchApp/ts/apps/search-all-app/state/interfaces/search-all-store.interface';
import { SearchAllResult } from 'SearchApp/ts/interfaces/search-all.interface';

import { SearchService } from 'SearchApp/ts/services/search.service';

import equal from 'Angular/utils/equal/deep-equal';

/**
 *
 * SearchAllTableComponent.
 *
 * This component is the table with search results. It has a link to get to specific results for a category.
 */
@Component({ standalone: false, selector: 'ycn-search-all-table', templateUrl: './search-all-table.template.html' })
export class SearchAllTableComponent implements OnDestroy {
    allSearchTypeSelected: boolean = false;

    perPage: number = 10; // when searching all categories is 10 but selecting "View All" for a category is 20

    queryInput: string = '';

    showError: boolean = false;

    resultHistory: Record<string, SearchAllResult> = {} as Record<string, SearchAllResult>;

    searchMethodInfoList: SearchTypeInfoPagination[] = [];

    searchMethodPagination: SearchCategoryPagination = {};

    searchResultsList: { category: SearchAllCategory; label: string; resultInfo: SearchAllResult }[] = [];

    searchTypes: SearchAllCategory[] = [];

    private storeSubscription: Subscription;

    constructor(private searchService: SearchService, private readonly store: Store<{ reducers: SearchAllRootStore }>) {
        this.storeSubscription = this.store.select('reducers').subscribe(this.mapStateToComponent);
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: SearchAllRootStore): void => {
        const newSearchTypes = getSearchTypes(state);
        const newQueryInput = getQueryInput(state).toString();
        const newSearchMethodPagination = getCategoryPageNum(state);

        if (
            (!equal(this.searchTypes, newSearchTypes) && newSearchTypes.length > 1) ||
            this.queryInput !== newQueryInput
        ) {
            this.perPage = 10;
        }
        this.searchTypes = newSearchTypes;
        this.searchMethodPagination = newSearchMethodPagination;
        this.queryInput = newQueryInput;

        this.searchMethodInfoList = SEARCH_ALL_METHODS.map((searchCategory) => ({
            ...searchCategory,
            page: this.searchMethodPagination[searchCategory.searchMethod] ?? 1,
        })).filter((searchCategory) => this.searchTypes.includes(searchCategory.searchMethod));

        this.allSearchTypeSelected =
            this.searchTypes.length === 0 || this.searchTypes.length === SEARCH_ALL_METHODS.length;

        const toFetch: [SearchAllCategory, number][] = [];
        this.searchMethodInfoList.forEach((search) => {
            // Check if we have the results for the current filters. If not, fetch a new object.
            const searchKey: string = this.getCurrentFiltersKey(search.searchMethod, search.page);
            if (
                !(searchKey in this.resultHistory) &&
                this.searchTypes.length !== 0 &&
                this.queryInput.trim().length !== 0
            ) {
                toFetch.push([search.searchMethod, search.page]);
            }
        });
        if (toFetch.length) {
            forkJoin(toFetch.map(([searchMethod, page]) => this.getSearchResults(searchMethod, page))).subscribe(
                (responses) => {
                    responses.forEach((results, index) => {
                        this.resultHistory[this.getCurrentFiltersKey(toFetch[index][0], toFetch[index][1])] = results;
                    });
                    this.buildResultsList();
                }
            );
        } else {
            this.buildResultsList();
        }
    };

    /**
     * Gets Search results using `searchType`, `queryInput`, `pageNum` and `perPage` as params.
     */
    getSearchResults(searchType: SearchAllCategory, pageNum: number): Observable<SearchAllResult> {
        return this.searchService.getSearchAllResults(searchType, this.queryInput, pageNum, this.perPage);
    }

    buildResultsList(): void {
        this.searchResultsList = this.searchMethodInfoList
            .filter(({ searchMethod, page }) => {
                const searchKey = this.getCurrentFiltersKey(searchMethod, page);
                return this.resultHistory[searchKey] && this.resultHistory[searchKey].results.length;
            })
            .map(({ searchMethod: category, page, label }) => {
                const searchKey = this.getCurrentFiltersKey(category, page);
                const resultInfo = this.resultHistory[searchKey];
                return {
                    category,
                    label,
                    resultInfo,
                };
            });
        this.showError = this.searchResultsList.length === 0;
    }

    /**
     * Resets the `searchType` filter setting all search types.
     */
    resetSearchType(): void {
        const searchTypes = SEARCH_ALL_METHODS.map((item) => item.searchMethod);
        this.store.dispatch(changeSearchTypes({ searchTypes }));
    }

    /**
     * Update page number of the search method.
     */
    pageChanged(pageNum: number, searchKey: SearchAllCategory): void {
        this.searchMethodPagination = {
            ...this.searchMethodPagination,
            [searchKey]: pageNum,
        };
        this.store.dispatch(searchChangeCategoryPageNum({ searchCategoryPageNum: this.searchMethodPagination }));
    }

    setCategorySearch(category: SearchAllCategory): void {
        this.perPage = 20;
        this.store.dispatch(changeSearchTypes({ searchTypes: [category] }));
    }

    /**
     * Returns a key for the results entry in `resultHistory`.
     */
    getCurrentFiltersKey(searchType: SearchAllCategory, pageNum: number): string {
        return `${searchType}-${this.queryInput}-${pageNum}-${this.perPage}`;
    }
}
